// Wrapper-side bridge for the ws-shim living inside the iframe.
//
// Design:
//
//   * The iframe's ws-shim replaces `window.WebSocket`. When the app
//     calls `new WebSocket(url, protocols)` for a URL destined to the
//     box, the shim posts `{type: "p2claw:ws", kind: "upgrade", wsId,
//     url, protocols}` to `window.parent`.
//   * This bridge (running in the wrapper) receives those messages,
//     opens a WS stream on the translator client, and proxies frames
//     back to the iframe via `iframe.contentWindow.postMessage`.
//   * `send` from the iframe becomes a `ws_msg` frame; `close` becomes
//     a `ws_close` frame. `ws_accept` / `ws_msg` / `ws_close` coming
//     from the box are posted back to the iframe as `p2claw:ws`
//     envelopes of the corresponding kind.
//
// The shim<->bridge envelope shape is the counterpart of the
// `WrapperSender` / `WrapperToShim` contracts in `../ws-shim.ts`. The
// two files intentionally don't share types so the ws-shim bundle
// stays small and doesn't drag in the bridge.

import { WsOpcode, type Frame } from "../wire/index.js";
import type { TranslatorClientLike } from "./session.js";

// Hoisted codecs — constructing one per message measurably costs on
// chatty sockets.
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/** Envelope sent by the iframe shim → wrapper bridge. */
export type ShimToWrapper =
  | {
      type: "p2claw:ws";
      kind: "upgrade";
      wsId: number;
      url: string;
      protocols: readonly string[];
    }
  | {
      type: "p2claw:ws";
      kind: "send";
      wsId: number;
      binary: boolean;
      data: Uint8Array;
    }
  | {
      type: "p2claw:ws";
      kind: "close";
      wsId: number;
      code: number;
      reason: string;
    };

/** Envelope sent by the wrapper bridge → iframe shim. */
export type WrapperToShim =
  | { type: "p2claw:ws"; kind: "open"; wsId: number; protocol: string }
  | {
      type: "p2claw:ws";
      kind: "message";
      wsId: number;
      binary: boolean;
      data: Uint8Array;
    }
  | {
      type: "p2claw:ws";
      kind: "close";
      wsId: number;
      code: number;
      reason: string;
    }
  | { type: "p2claw:ws"; kind: "error"; wsId: number; message: string };

export interface BridgeOptions {
  iframe: { contentWindow: Window | null };
  translator: TranslatorClientLike;
  window: Window & typeof globalThis;
  /** Resolve the outgoing `Cookie` header for a WS handshake URL (from
   *  the SW's jar ⊕ cookieStore). Browsers can't set `Cookie` on a
   *  `WebSocket()` and the handshake never touches the SW fetch path, so
   *  the SW-owned cookies would otherwise be absent — breaking cookie /
   *  session-authenticated WebSockets (e.g. Immich's realtime socket).
   *  Omitted / resolving `""` → the handshake proceeds without it. */
  getCookieHeader?: (url: string) => Promise<string>;
}

interface ActiveStream {
  streamId: number;
  send: (frame: Frame) => void | Promise<void>;
}

/**
 * Install the bridge. Returns an uninstaller. Safe to call once per
 * wrapper lifetime; the iframe's shim is expected to use the same
 * `wsId` space for the session (starts at 1 and monotonically
 * increases on the shim side).
 */
export function installWsBridge(opts: BridgeOptions): () => void {
  const { iframe, translator, window: win, getCookieHeader } = opts;
  const active = new Map<number, ActiveStream>();
  // wsIds whose `close` arrived while the async upgrade (cookie fetch)
  // was still in flight — the upgrade aborts instead of opening a stream
  // that nothing will ever close.
  const closedBeforeUpgrade = new Set<number>();

  // `transfer` detaches the listed buffers on this side — only pass
  // buffers the bridge no longer touches.
  const postToShim = (msg: WrapperToShim, transfer?: Transferable[]): void => {
    iframe.contentWindow?.postMessage(msg, "*", transfer);
  };

  const onMessage = (ev: MessageEvent): void => {
    if (ev.source !== iframe.contentWindow) return;
    const data = ev.data as Partial<ShimToWrapper> | null;
    if (!data || data.type !== "p2claw:ws") return;
    switch (data.kind) {
      case "upgrade": {
        if (
          typeof data.wsId !== "number" ||
          typeof data.url !== "string"
        ) {
          return;
        }
        const wsId = data.wsId;
        const protocols = Array.isArray(data.protocols) ? data.protocols : [];
        const parsed = (() => {
          try {
            return new URL(data.url!, win.location.href);
          } catch {
            return null;
          }
        })();
        if (!parsed) {
          postToShim({
            type: "p2claw:ws",
            kind: "error",
            wsId,
            message: `invalid ws url: ${data.url}`,
          });
          return;
        }
        // Build the upgrade headers. We carry the Sec-WebSocket-Protocol
        // list and the Host so the box can present them to the app, plus
        // the SW-owned Cookie (jar ⊕ cookieStore) so cookie-authenticated
        // WebSockets ride the same session as the app's HTTP requests.
        // The cookie fetch is bounded (SW side ~500ms); a miss just sends
        // the handshake bare, so a slow/dead port never blocks the WS.
        void (async () => {
          const headers: Array<readonly [string, string]> = [
            ["host", parsed.host],
          ];
          if (protocols.length > 0) {
            headers.push(["sec-websocket-protocol", protocols.join(", ")]);
          }
          if (getCookieHeader) {
            let cookie = "";
            try {
              cookie = await getCookieHeader(parsed.href);
            } catch {
              /* bare handshake on failure */
            }
            if (cookie) headers.push(["cookie", cookie]);
          }
          // A `close` may have raced in while we awaited the cookie.
          if (closedBeforeUpgrade.has(wsId)) {
            closedBeforeUpgrade.delete(wsId);
            return;
          }
          const stream = translator.openWebSocket({
            path: parsed.pathname + parsed.search,
            headers,
            onFrame: (frame) => dispatchBoxFrame(wsId, frame),
          });
          active.set(wsId, stream);
        })();
        break;
      }
      case "send": {
        const stream = active.get(data.wsId!);
        if (!stream) return;
        const bytes =
          data.data instanceof Uint8Array ? data.data : new Uint8Array(0);
        void stream.send({
          type: "ws_msg",
          streamId: stream.streamId,
          opcode: data.binary ? WsOpcode.Binary : WsOpcode.Text,
          payload: bytes,
        });
        break;
      }
      case "close": {
        const stream = active.get(data.wsId!);
        if (!stream) {
          // Close raced ahead of the async upgrade — flag it so the
          // in-flight upgrade aborts instead of opening an orphan stream.
          if (typeof data.wsId === "number") closedBeforeUpgrade.add(data.wsId);
          return;
        }
        void stream.send({
          type: "ws_close",
          streamId: stream.streamId,
          code: data.code ?? 1000,
          reason: textEncoder.encode(data.reason ?? ""),
        });
        translator.closeWebSocket(stream.streamId);
        active.delete(data.wsId!);
        break;
      }
    }
  };

  function dispatchBoxFrame(wsId: number, frame: Frame): void {
    switch (frame.type) {
      case "ws_accept": {
        const protocol = headerValue(frame.headers, "sec-websocket-protocol");
        postToShim({
          type: "p2claw:ws",
          kind: "open",
          wsId,
          protocol,
        });
        break;
      }
      case "ws_msg": {
        const binary = frame.opcode === WsOpcode.Binary;
        // `frame.payload` is freshly allocated by decode (exact-sized
        // buffer) and unused after this — transfer instead of clone.
        postToShim(
          {
            type: "p2claw:ws",
            kind: "message",
            wsId,
            binary,
            data: frame.payload,
          },
          [frame.payload.buffer as ArrayBuffer],
        );
        break;
      }
      case "ws_close": {
        const reason = textDecoder.decode(frame.reason);
        postToShim({
          type: "p2claw:ws",
          kind: "close",
          wsId,
          code: frame.code,
          reason,
        });
        active.delete(wsId);
        break;
      }
      case "err": {
        const message = textDecoder.decode(frame.message);
        postToShim({
          type: "p2claw:ws",
          kind: "error",
          wsId,
          message,
        });
        active.delete(wsId);
        break;
      }
      default:
        // Other frame types (req/res/data/end/ping/pong/goaway) are not
        // meaningful on a ws stream — ignore.
        break;
    }
  }

  win.addEventListener("message", onMessage);

  return () => {
    win.removeEventListener("message", onMessage);
    for (const { streamId } of active.values()) {
      translator.closeWebSocket(streamId);
    }
    active.clear();
  };
}

function headerValue(
  headers: ReadonlyArray<readonly [Uint8Array, Uint8Array]>,
  name: string,
): string {
  const target = name.toLowerCase();
  for (const [n, v] of headers) {
    if (textDecoder.decode(n).toLowerCase() === target) {
      return textDecoder.decode(v);
    }
  }
  return "";
}
